import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import { MdFilterList, MdRefresh, MdBook, MdHistory, MdAssignment, MdAssignmentTurnedIn } from 'react-icons/md';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { DateTime } from 'luxon';
import { asyncState, useAsync, useAuth, useForm } from '~/hooks';
import { orders, metrics } from '~/api';
import { DashboardContext, useDashboard } from './context';
import Header from './LawFirmSubheader';
import DashboardGrid from './DashboardGrid';
import FilterModal from './FilterModal';
import AttachmentsModal from './AttachmentsModal';
import RevisionsModal from './RevisionsModal';
import { applyFilters } from './dashboardGridFilters';
import { get } from 'lodash';
import { auth } from '~/util';

const LawFirmHome = () => {
    const [timespan, setTimespan] = useState(30);
    const [girdTimeSpan, setGirdTimeSpan] = useState(30);

    const backDate = useMemo(() => formatBackDate(timespan), [timespan]);
    const girdBackDate = useMemo(() => formatBackDate(girdTimeSpan), [girdTimeSpan]);

    const { user } = useAuth();

    const [activeTitleOpinionId, setActiveTitleOpinionId] = useState(null);

    const gridData = useAsync(async () => {
        const data = auth.checkRole(user.roleId, auth.ROLES.FIRM_ADMIN) ? await orders.getMyFirmData(girdBackDate) : await orders.getFirmData(girdBackDate, user.id);
        return flattenData(data);
    }, false);

    const policyMetrics = useAsync(() => metrics.policySearchCountsByUser(user.id, backDate), false);
    const titleMetrics = useAsync(() => metrics.titleOpinionCountsByUser(user.id, backDate), false);

    const [filtersOpen, setFiltersOpen] = useState(false);
    
    const {
        state: { values: filters },
        fieldActions,
        formActions
    } = useForm({
        initialState: {
            filenumber: '',
            ownerName: '',
            submissionStart: null,
            submissionEnd: null,
            address: '',
            county: '',
            statusOptions: [
                { label: 'Prior Policy Requested', value: 'p-2' },
                { label: 'Prior Policy Found', value: 'p-3' },
                { label: 'Prior Policy Not Found', value: 'p-4' },
                { label: 'PTO Started', value: 't-1' },
                { label: 'PTO Submitted', value: 't-2' },
                { label: 'PTO Revision Submitted', value: 't-5' },
                { label: 'FTO Started', value: 't-3' },
                { label: 'FTO Submitted', value: 't-4' },
                { label: 'Canceled', value: 't-6' },
                { label: 'SFTO Started', value: 't-7' },
                { label: 'SFTO Submitted', value: 't-8' }
            ],
            status: null
        },
    });

    let [filteredRows, setFilteredRows] = useState([]);

    const clean = (filters) => {
        return omitBy(filters, (value, key) => value == null || /options/i.test(key));
    };

    const filterData = () => {
        const filtered = applyFilters(gridData.value, clean(filters));
        setFilteredRows(filtered);
        setFiltersOpen(false);
    };

    const clearFilters = () => {
        formActions.reset();
        setFilteredRows(gridData.value);
        setFiltersOpen(false);
    };

    const [attachmentsOpen, setAttachmentsOpen] = useState(false);
    const showAttachmentsModal = () => setAttachmentsOpen(true);
    const hideAttachmentsModal = () => setAttachmentsOpen(false);
    const [attachments, setAttachments] = useState([]);

    const fetchAll = async () => gridData.execute()
        .then(() => policyMetrics.execute())
        .then(() => titleMetrics.execute());

    const fetchGridData = async () => gridData.execute();
    useEffect(fetchAll, [timespan]);
    useEffect(fetchGridData, [girdTimeSpan]);
    useEffect(() => setFilteredRows(gridData.value), [gridData.value, gridData.status]);

    const [revisionsOpen, setRevisionsOpen] = useState(false);
    const showRevisionsModal = () => setRevisionsOpen(true);
    const hideRevisionsModal = () => setRevisionsOpen(false);

    const ctxValue = {
        activeTitleOpinionId, 
        setActiveTitleOpinionId,
        gridData,
        policyMetrics,
        titleMetrics,
        fetchAll,
        filteredRows,
        filters,
        fieldActions,
        apply: filterData,
        clear: clearFilters,
        timespan,
        setTimespan,
        girdTimeSpan,
        setGirdTimeSpan,
        attachments,
        setAttachments,
        showAttachmentsModal,
        hideAttachmentsModal,
        showRevisionsModal,
        hideRevisionsModal
    };

    return (
        <DashboardContext.Provider value={ctxValue}>
            <Header />
            <div className="container py-4">
                <RefreshIndicator />
                <div className='flex items-center'>
                    <button 
                        className='py-2 px-4 flex text-blue-dark text-sm font-semibold bg-gray-50 focus:outline-none items-center justify-center rounded-full'
                        onClick={() => setFiltersOpen(true)}
                    >
                        <MdFilterList size='1.125em' className='mr-1'/>
                        Filter
                    </button>
                    
                    <div className="flex items-center justify-end ml-auto">
                        <Link to='/policySearchHistory' className="mr-4 py-2 px-4 flex items-center justify-center text-blue-50 text-sm font-semibold">
                            <MdHistory className="mr-1" size="1.25em"/> Search History
                        </Link>
                        <Link to='policysearchrequest' className='py-2 px-4 flex items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'>
                            <MdBook className="mr-2" size="1.025em"/> Request Prior Policy
                        </Link>
                        <Link to='/preliminaryopinion/0' className='ml-4 py-2 px-4 flex items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'>
                            <MdAssignment className="mr-2" size="1.125em"/> Create Preliminary Opinion
                        </Link>
                        <Link to='/finalopinion/0' className='ml-4 py-2 px-4 flex items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'>
                            <MdAssignmentTurnedIn className="mr-2" size="1.125em"/> Create Final Opinion
                        </Link>                       
                        <Link to='/StraightFinal/0' className='ml-4 py-2 px-4 flex items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'>
                            <MdAssignment className="mr-2" size="1.125em"/> Create Straight to Final
                        </Link>
                    </div>
                </div>
                <DashboardGrid />
                <FilterModal isOpen={filtersOpen} setOpen={setFiltersOpen} />
                <AttachmentsModal isOpen={attachmentsOpen} />
                <RevisionsModal isOpen={revisionsOpen} setIsOpen={setRevisionsOpen} activeTitleOpinionId={activeTitleOpinionId} />
            </div>
        </DashboardContext.Provider>
    );
};

export default LawFirmHome;

function RefreshIndicator() {
    const { fetchAll } = useDashboard();

    const [lastUpdated, setLastUpdated] = useState(DateTime.now());

    const refresh = async () => {
        await fetchAll();
        setLastUpdated(DateTime.now());
    };

    return (
        <div className='flex items-center text-sm mr-auto my-2'>
            {status != asyncState.IDLE && (
                <>
                    <p className="font-bold">Last Updated</p>
                    <p className="ml-2">{lastUpdated.toFormat("DD 'at' tt")}</p>
                </>
            )}
            {status == asyncState.PENDING 
                ? <AiOutlineLoading3Quarters className='m-auto text-blue-50 animate-spin ml-2'/>
                : (
                    <button 
                        className="flex items-center justify-center text-blue-50 font-semibold p-2 ml-4 focus:outline-none"
                        onClick={refresh}
                    >
                        <MdRefresh className="mr-1" size="1.25em"/>
                        Refresh
                    </button>
                )
            }
        </div>
    );
}

function formatBackDate(timespan) {
    let backDate = DateTime.utc();
    // adding 1 to the .minus below since the stored proc does >= when comparing dates
    switch (timespan) {
    case 7:
        backDate = backDate.minus({ days: 8 });
        break;
    case 30:
        backDate = backDate.minus({ days: 31 });
        break;
    case 365:
        backDate = backDate.minus({ days: 366 });
        break;
    case 1:
        break;
    default:
        backDate = backDate.minus({ years: 100 });
    }

    if (backDate != null)
        backDate = backDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0});

    return backDate ? backDate.toISO() : null;
}

function flattenData(data = []) {
    if (data.length < 1)
        return data;

    return data
        .map(d => {

            const status = flattenStatus(d);

            return {
                filenumber: d.fileNumber,
                orderNumber: isEmpty(d.titleOpinion) ? null : d.titleOpinion.orderNumber,
                status,          
                submitted: flattenSubmitted(d),
                modified: flattenModified(d),
                streetAddress: flattenAddress(d, status),
                owners: flattenOwners(d),
                attachments: flattenDocuments(d),
                titleOpinion: d.titleOpinion,
                policySearch: d.policySearch
            };
        })
        .sort((a, b) => {
            if (a.modified > b.modified)
                return -1;
            return 1;
        });
}

function flattenStatus(data) {
    const prefix = isEmpty(data.titleOpinion) ? 'p' : 't';

    if (prefix == 'p') {
        return data.policySearch.statusId > 2
            ? `p-${data.policySearch.statusId}`
            : 'p-2';
    }
    return `${prefix}-${data.titleOpinion.statusId}`;
}

function flattenSubmitted(data) {
    return isEmpty(data.titleOpinion)
        ? data.policySearch.submitted
        : data.titleOpinion.submitted;
}

function flattenModified(data) {
    let  rawTitleOpinionModified = get(data, 'titleOpinion.modified', null);
    if (rawTitleOpinionModified == null)
        rawTitleOpinionModified = get(data, 'titleOpinion.created', null);

    let rawPolicySearchModified = get(data, 'policySearch.modified', null);
    if (rawPolicySearchModified == null)
        rawPolicySearchModified = get(data, 'policySearch.created', null);

    let titleOpinionModified = null, policySearchModified = null;

    if (rawTitleOpinionModified != null)
        titleOpinionModified = DateTime.fromISO(rawTitleOpinionModified);
    
    if (rawPolicySearchModified != null)
        policySearchModified = DateTime.fromISO(rawPolicySearchModified);

    return titleOpinionModified > policySearchModified
        ? titleOpinionModified
        : policySearchModified;
}

function flattenAddress(data, status) {

    if (/^p-[1-9]{1}$/i.test(status)) {
        const { streetNumber, streetName, streetSuffix } = data.policySearch;
        return `${streetNumber} ${streetName} ${streetSuffix}`.trimEnd();
    }
    if (/^t-[1-8]{1}$/i.test(status)) {
        const { streetAddress } = data.titleOpinion;
        return `${streetAddress}`.trimEnd();
    }

    const propAddress = get(data, 'titleOpinion.propertyAddress', null);
    const streetAddress = get(data, 'titleOpinion.streetAddress', null);

    if (/^t-[12]{1}$/i.test(status)) {
       
        return propAddress == null
            ? streetAddress
            : propAddress;   
                 
    }
    

    if (/^t-[34]{1}$/i.test(status)) {
        if (propAddress != null)
            return propAddress;
        if (streetAddress != null)
            return streetAddress;
       
        if (!isEmpty(data.policySearch)) {
            const { streetNumber, streetName, streetSuffix } = data.policySearch;
            return `${streetNumber} ${streetName} ${streetSuffix}`.trimEnd();
        }
    }

    return null;
}

function flattenOwners(data) {
    let owners = [];

    if (!isEmpty(data.titleOpinion)) {
        owners = tryMapTitleOpinionOwners(data.titleOpinion);

    } else if (!isEmpty(data.policySearch)) {
        owners = tryMapPolicySearchOwners(data.policySearch);
    }

    return owners;
}

function tryMapTitleOpinionOwners(titleOpinion) {
    if (Array.isArray(titleOpinion.owners)) {

        return titleOpinion.owners.map((o => {
            let owner = {};
            if (o.entity) {
                owner.entityName = o.entity.name;
            }
            if (o.primary) {
                owner.primary = { ...o.primary };
            }
            if (o.secondary) {
                owner.secondary = { ...o.secondary };
            }

            return owner;
        }));
    }

    return [];
}

function tryMapPolicySearchOwners(policySearch) {
    const owners = [];

    const {
        ownerFirstName,
        ownerMiddleName,
        ownerLastName
    } = policySearch;

    owners.push({
        firstName: ownerFirstName,
        middleName: ownerMiddleName,
        lastName: ownerLastName
    });

    return owners;
}

function flattenDocuments(data) {
    let docs = [];

    const { titleOpinion, policySearch } = data;

    if (!isEmpty(policySearch)) {
        if (!isEmptyArray(policySearch.documents)) {
            docs.push(...policySearch.documents);
        }
    }

    if (!isEmpty(titleOpinion)) {
        if (!isEmptyArray(titleOpinion.documents)) {
            docs.push(...titleOpinion.documents);
        }

        if (!isEmptyArray(titleOpinion.sections)) {
            const sectionDocs = parseSectionDocs(titleOpinion.sections);
            docs.push(...sectionDocs);
        }
    }

    return docs;
}

function parseSectionDocs(sections) {
    let docs = [];

    for (const s of sections) {
        if (!isEmptyArray(s.documents)) {
            docs.push(...s.documents);
        }
        
        if (!isEmptyArray(s.details)) {
            for (const d of s.details) {
                if (!isEmptyArray(d.documents)) {
                    docs.push(...d.documents);
                }
            }
        }
    }
    
    return docs;
}

function isEmptyArray(arr) {
    return !Array.isArray(arr) || arr.length < 1;
}